import React from 'react';
import { Box, Typography, Stack, Skeleton, IconButton, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import type { PriorityItem } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import DriverPill from '../DriverPill';
import PriorityChip from '../PriorityChip';
import WhyAffordance from '../ai/WhyAffordance';
import { WorkItemTypeBadge, workItemTypeHint } from '../workItems/WorkItemTypeTreatment';
import type { RationaleEntry } from '../../utils/aiRationale';
import { dashboardCopy } from '../../copy/dashboard';

// ---------------------------------------------------------------------------
// PriorityList — three-at-most list of what matters today. Each row carries
// a driver pill so strategic context is visible without a drill-down, plus
// a "Why?" affordance showing the AI reasoning behind the ordering when
// available. When `onComplete` is wired, each row gains a tick-to-complete
// affordance so the user does not have to navigate to /work-items or hit
// Replan just to see a completion reflected.
// ---------------------------------------------------------------------------

interface PriorityListProps {
  items: PriorityItem[];
  loading?: boolean;
  /** workItemId → { rationale, strategyLink }. Optional — rows without an
   *  entry simply hide the affordance. */
  rationaleMap?: Map<string, RationaleEntry>;
  /** When supplied, each row shows a tick button that marks the item Done
   *  and lets the host refresh local state. Host is responsible for the
   *  optimistic update and any API call. */
  onComplete?: (workItemId: string) => void;
}

export function PriorityList({ items, loading = false, rationaleMap, onComplete }: PriorityListProps) {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600, mb: 1.5, display: 'block' }}
      >
        {dashboardCopy.prioritiesHeader}
      </Typography>

      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No priorities set for today.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Box
              key={item.workItem.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/work-items?id=${item.workItem.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/work-items?id=${item.workItem.id}`);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              {/* Scheduled time (tabular so columns align down the list). */}
              <Box
                sx={{
                  minWidth: 52,
                  fontSize: 13,
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 500,
                }}
              >
                {item.scheduledTime ?? '—'}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <WorkItemTypeBadge type={item.workItem.type} variant="compact" />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {item.workItem.title}
                  </Typography>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                    <WhyAffordance
                      rationale={rationaleMap?.get(item.workItem.id)?.rationale}
                      secondary={rationaleMap?.get(item.workItem.id)?.strategyLink}
                      size="compact"
                      label="Why this priority"
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <DriverPill driver={item.workItem.strategicDriver} size="compact" />
                  {item.plannedMinutes !== undefined && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatMinutes(item.plannedMinutes)}
                    </Typography>
                  )}
                  {(() => {
                    const hint = workItemTypeHint(item.workItem);
                    return hint ? (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {hint}
                      </Typography>
                    ) : null;
                  })()}
                </Stack>
              </Box>

              {/* Tick-to-complete. Shown only when the host has wired
                  onComplete; click is stop-propagated so the row's navigate
                  does not fire on the same click. Done / Cancelled rows
                  should never appear here (derivePriorities filters them)
                  but we guard anyway. */}
              {onComplete && item.workItem.status !== 'Done' && item.workItem.status !== 'Cancelled' && (
                <Tooltip title="Mark complete">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onComplete(item.workItem.id);
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <PriorityChip priority={item.workItem.priority} />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default PriorityList;
